/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import { getNormalizedPropertyName } from '../utils/form-handler';
import Utils from '../utils/utils';
import CascadeManager from '../cascade/manager';
import FormUtils from '../form/form-utils';
import ContextTreeManager from '../contextree/treemanager';
import * as StateManager from '../store/state/state-manager';
import { getContainerItemData, getTargetFromContainerItemID } from '../container/container-utils';
import ContextProcessor from '../context/context-processor';
import type { NodeProps } from '../contextree/types';
import { publicConstants } from '../constants';

import type StateMachine from './state-machine/state-machine';
import type { StateNodeInstruction } from './state-machine/state-node';

import { getInstructions, createContextEntry } from './index';

class StateMachineManager {
  private states: { [key: string]: any };
  private unConnectedStates: { [key: string]: any };
  constructor() {
    /* states : {
      'app/primary/workarea1': {
        'caseinfo.content.x.employeeList' : StateMachine,
        'caseinfo.content.y.employeeList' : StateMachine
      }
    } */
    this.states = {};
    this.unConnectedStates = {};
  }

  getStateMachine(context: string, pageReference: string, target: string) {
    return (
      this.states?.[context]?.[`${pageReference}${target}`] ||
      this.unConnectedStates?.[context]?.[`${pageReference}${target}`]
    );
  }

  getAllStateMachines(context: string, pageReference: string, target: string) {
    return {
      states: this.states?.[context]?.[`${pageReference}${target}`],
      unConnectedStates: this.unConnectedStates?.[context]?.[`${pageReference}${target}`]
    };
  }

  getAllStateMachinesByContext(context: string) {
    return {
      states: this.states?.[context],
      unConnectedStates: this.unConnectedStates?.[context]
    };
  }

  isStateMachineExist(context: string, pageReference: string, target: string) {
    return (
      !!this.states?.[context]?.[`${pageReference}${target}`] ||
      !!this.unConnectedStates?.[context]?.[`${pageReference}${target}`]
    );
  }

  getUnConnectedStateMachines(context: string) {
    return this.unConnectedStates?.[context];
  }

  setStateMachine(context: string, target: string, pageReference: string, stateMachine: StateMachine) {
    const targetPage = `${pageReference}${target}`;
    const { stateMachine: parentStateMachine } = this.getStateMachineObjByPath(targetPage, context, true, true);
    const updateStates = (key: string, value?: StateMachine, isConnectedStateType: boolean = false) => {
      const statesObj = isConnectedStateType ? this.states : this.unConnectedStates;

      if (!statesObj[context]) {
        statesObj[context] = {};
      }

      if (!statesObj[context][key]) {
        statesObj[context][key] = {};
      }
      statesObj[context][key] = value;
    };
    if (parentStateMachine && parentStateMachine.targetPageReference !== stateMachine.targetPageReference) {
      parentStateMachine.addChild(stateMachine);
      updateStates(targetPage, stateMachine);
      return;
    }

    const matchedChildStateMachines = Object.entries(this.states[context] || {})
      .filter(([key]) => {
        if (key.includes(targetPage)) {
          const matchedKeyStringLength = key.substring(0, targetPage.length).length;
          return (
            key.substring(matchedKeyStringLength).startsWith('[') ||
            key.substring(matchedKeyStringLength).startsWith('.')
          );
        }
        return false;
      })
      .reduce((prev, [key, value]) => ({ ...prev, [key]: value }), {});
    // Check if any child target exists in connected states and move them to unconnected states
    if (Object.keys(matchedChildStateMachines)?.length) {
      Object.entries(matchedChildStateMachines).forEach(([key, value]) => {
        delete this.states[context][key];
        stateMachine.addChild(value as unknown as StateMachine);
        updateStates(key, value as unknown as StateMachine);
      });
      this.states[context][targetPage] = stateMachine;
    } else {
      updateStates(targetPage, stateMachine, true);
    }
  }

  buildPageInstructionsByContext(context: string, isOnlyChanged?: boolean) {
    const states = this.getStateMachinesByContext(context) || {};
    const instructions: any[] = [];
    Object.keys(states)
      .sort()
      .forEach((target) => {
        const targetNode = ContextTreeManager.getContextReferences(context)?.get(target);
        const resolvedProps: NodeProps =
          (targetNode && ContextProcessor.resolveContextTreeNodeProps(targetNode.props)) || {};
        const stateMachine = states[target];
        if (targetNode && resolvedProps.readOnly !== true) {
          const inst = stateMachine.buildSubmitPageInstructions(isOnlyChanged);
          instructions.push(...inst);
        }
        // clear statemachine if node doesn't exist(invisible)
        if (!targetNode) {
          this.setStateMachineEmpty(context, target);
        }
      });
    return instructions;
  }

  buildClientChangeList(context: string) {
    const states = this.getStateMachinesByContext(context) || {};
    let changeList = {};
    const resultObject = {};
    Object.keys(states).forEach((target) => {
      const stateMachine = states[target];
      const inst = stateMachine.getChangeList();
      changeList = { ...changeList, ...inst };
    });
    Utils.flatten(changeList, '', resultObject);
    return resultObject;
  }

  getStateMachinesByContext(context: string | null) {
    return context ? this.states?.[context] : undefined;
  }

  clearStateMachines(context: string) {
    this.states[context] = {};
    this.unConnectedStates[context] = {};
  }

  setStateMachineEmpty(context: string, target: string) {
    const stateMachine = this.getStateMachine(context, target, '');
    stateMachine.root = null;
    stateMachine.current = null;
    const unConnectedStateMachines = this.getUnConnectedStateMachines(context) || {};
    Object.keys(unConnectedStateMachines).forEach((key) => {
      if (key.includes(target)) {
        delete unConnectedStateMachines[key];
      }
    });
  }

  removeStateMachine(context: string, target: string) {
    delete this.states[context]?.[target];
  }

  createStateMachine(fullReference: string, context: string) {
    let parentPageReference = fullReference.substring(0, fullReference.lastIndexOf('['));
    const parenTarget = parentPageReference.substring(parentPageReference.lastIndexOf('.'));
    parentPageReference = parentPageReference.substring(0, parentPageReference.lastIndexOf('.'));

    if (parentPageReference && parenTarget) {
      createContextEntry(context, parenTarget, parentPageReference);
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  getStateMachineObjByPath(
    stateMachineTarget: string,
    context: string,
    processList: boolean,
    skipCreateStateMachine = false
  ) {
    const regex = /\[\d+\]$/gm;
    const fullReference = stateMachineTarget;
    let index;
    while (stateMachineTarget) {
      if (this.isStateMachineExist(context, '', stateMachineTarget)) {
        break;
      }
      const target = getTargetFromContainerItemID(context);
      const itemData = getContainerItemData(target, context) || {};
      const isPageGroup = itemData?.isPageGroup || itemData?.subscriptFieldName || typeof itemData?.index === 'string';
      index = null;
      if (processList && stateMachineTarget.endsWith(']')) {
        index = stateMachineTarget.match(regex);
        index = index?.[index.length - 1]?.substring(1, index[index.length - 1].length - 1);
        [stateMachineTarget] = stateMachineTarget.split(regex);

        if (!skipCreateStateMachine && !this.isStateMachineExist(context, '', stateMachineTarget)) {
          // create state machine
          this.createStateMachine(fullReference, context);
          break;
        }
      } else if (itemData?.index && stateMachineTarget.includes(itemData.index) && isPageGroup) {
        const parts = stateMachineTarget.split('.');
        index = parts[3];
        stateMachineTarget = parts.slice(0, 3).join('.');
        break;
      } else {
        stateMachineTarget = stateMachineTarget.substring(0, stateMachineTarget.lastIndexOf('.'));
      }
    }
    if (!stateMachineTarget) {
      return {};
    }

    return {
      stateMachineTarget,
      stateMachine: this.getStateMachine(context, '', stateMachineTarget),
      index
    };
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  executeStateMachine(
    context: string,
    propertyPath: string,
    value: any,
    processList = true,
    options: { skipStateUpdate?: boolean; skipDirtyValidation?: boolean } = {}
  ) {
    const { stateMachineTarget, index } = this.getStateMachineObjByPath(propertyPath, context, processList);
    if (!stateMachineTarget) {
      return false;
    }

    const target = getTargetFromContainerItemID(context);
    const itemData = getContainerItemData(target, context) || {};

    let [, contentKey] = propertyPath.split(stateMachineTarget);
    if (!contentKey) {
      return false;
    }
    const isPageGroup =
      (itemData?.subscriptFieldName && propertyPath.includes(itemData?.index)) || itemData?.isPageGroup;
    if (index !== null) {
      if (isPageGroup) {
        [, contentKey] = contentKey.split(/(?=\.\w+)/);
      } else {
        [, contentKey] = contentKey.split(/^\[\d+\]/gm);
      }
    }

    const payload = {
      [getNormalizedPropertyName(contentKey)]: value
    };
    const stateMachine = this.getStateMachine(context, '', stateMachineTarget);
    if (index !== null) {
      if (isPageGroup) {
        const existingInstructions = getInstructions(context);
        if (itemData?.resourceStatus === 'CREATE') {
          existingInstructions.forEach((item) => {
            if (
              item.instruction === 'UPDATE' &&
              item.groupIndex === itemData.index &&
              !item.content[itemData.subscriptFieldName]
            ) {
              stateMachine.update(
                { [itemData.subscriptFieldName]: itemData.index },
                index,
                options.skipStateUpdate ?? true,
                options
              );
            }
          });
        }
        stateMachine?.update(payload, index, options.skipStateUpdate ?? true, options);
      } else {
        stateMachine?.update(payload, Number(index) + 1, options.skipStateUpdate ?? true, options);
      }
    } else {
      stateMachine?.updatePage(Utils.unflatten(payload));
    }

    return true;
  }

  setUniqueField(context: string, pageInstruction: StateNodeInstruction, fullReference: string) {
    const uniqueField = ContextTreeManager.getContextReferences(context)?.get(fullReference)?.props?.uniqueField;

    if (uniqueField) {
      if (!pageInstruction.content) {
        pageInstruction.content = {};
      }
      pageInstruction.content[getNormalizedPropertyName(uniqueField)] = crypto.randomUUID();
    }
  }

  getBasePageReference(context: string) {
    const dataInfoContent = StateManager.getValue(publicConstants.DATA_INFO.DATA_INFO_CONTENT, '', context);

    return dataInfoContent ? publicConstants.DATA_INFO.DATA_INFO_CONTENT : publicConstants.CASE_INFO.CASE_INFO_CONTENT;
  }

  mergeServerPageInstructions(context: string, serverSidePageInstructions: StateNodeInstruction[]) {
    serverSidePageInstructions.forEach((pageInstruction) => {
      if (pageInstruction.target) {
        const targetReference = Utils.convertClipboardPageToPageRefNotation(pageInstruction.target);
        const basePageReference = this.getBasePageReference(context);
        const fullReference = `${basePageReference}${targetReference}`;
        if (!this.isStateMachineExist(context, fullReference, '')) {
          const target = fullReference.substring(fullReference.lastIndexOf('.'));
          const pageReference = fullReference.substring(0, fullReference.lastIndexOf('.'));
          createContextEntry(context, target, pageReference);
        }
        const stateMachine = this.getStateMachine(context, fullReference, '');
        if (pageInstruction.instruction === 'INSERT') {
          this.setUniqueField(context, pageInstruction, fullReference);
        }
        stateMachine?.insertNode(pageInstruction);
      }
    });
  }

  /**
   * This will help us to subscribe a callback when pageList property is update in the redux because of server changes.
   * @private
   */
  registerForServerChanges() {
    CascadeManager.registerForListChanges((context: string, propertyReference: string, value: any) => {
      /*
      we are not interested to generate update instruction on change of classID because we shouldn't pass classID in the
      page instructions request body while submit.
      When adding a new row in pyRefreshData, generating UPDATE Page Instructions fails due to delayed Context Node creation.
      To address this, we've added a setTimeout to ensure the Context Node is ready before checking FormUtils.isPropertyEditable
      and generating the instructions.
      TODO: We have to explore for more sophisticated approach to eliminate setTimeout.
      */
      setTimeout(() => {
        this.generatePageInstructions(context, propertyReference, value);
      }, 0);
    });
  }

  /**
   * This will help us to subscribe a callback when pageList property is update in the redux because of server changes
   * synchronously.
   * @private
   */
  registerForServerChangesSync() {
    CascadeManager.registerForListChanges((context: string, propertyReference: string, value: any) => {
      this.generatePageInstructions(context, propertyReference, value);
    });
  }

  /**
   * This will generate UPDATE Page Instructions for the propertyReference in the given context with the provided value.
   * @private
   */
  generatePageInstructions(context: string, propertyReference: string, value: any) {
    const splitForMultiselect = '].';
    if (!propertyReference.includes('.classID') && FormUtils.isPropertyEditable(context, propertyReference)) {
      this.executeStateMachine(context, propertyReference, value);
    }
    if (!propertyReference.includes('.classID') && propertyReference.includes(splitForMultiselect)) {
      const parentPageListName = propertyReference.substring(0, propertyReference.lastIndexOf('['));
      const editableFields = FormUtils.getEditableFields(context);
      if (
        ['multiselect', 'checkbox'].includes(
          editableFields.find((fieldObject) => fieldObject.name === parentPageListName)?.type
        )
      ) {
        const formFieldMap = ContextTreeManager.getContextReferences(context);
        const fieldNode = formFieldMap?.get(parentPageListName);
        const propertyName = propertyReference.substring(
          propertyReference.lastIndexOf(splitForMultiselect) + splitForMultiselect.length
        );
        if (fieldNode?.props.selfReferencingDatapageKeys?.includes(propertyName)) {
          this.executeStateMachine(context, propertyReference, value);
        }
      }
    }
  }

  /**
   * This will help us to un-subscribe all the existing callbacks.
   * @private
   */
  unRegisterForServerChanges() {
    CascadeManager.unRegisterForListChanges();
  }
}
export default new StateMachineManager();
